/**
 * Locks the search-param contract for the Close hub sub-routes, the
 * workspace, and CPH-011's dispatcher. A new param goes here first; route
 * `validateSearch` blocks consume the parsers below so invalid values
 * never escape as runtime crashes.
 */
import {
  BankTransactionSignFilter,
  BankTransactionStatusFilter,
} from '@signalvote/shared';
import type { StatusPivot } from '@/components/finance/transactions/StatusPivotChips';

// ────────────────────────────────────────────────────────────────
// Param value types
// ────────────────────────────────────────────────────────────────

/**
 * MRP rendering mode. `'classify'` is the bank-lines workspace view
 * (rev 3 CPH-019 unified table); `'deposit-match'` is the Phase 2
 * deposit-match Reconcile view.
 */
export type ViewParam = 'classify' | 'deposit-match';

/** CPH-011 dispatcher action keys. */
export type ActionParam = 'refund' | 'rapid';

/** CPH-011 "Back to report" round-trip marker. */
export type ReturnToParam = 'report';

// USI-005 (ADR-011): CPH-018 Phase-2 Reconcile tabbed shell + the
// associated `?tab=` namespace + the four-tab union were retired with
// the reconciliation workspace deletion.

// ────────────────────────────────────────────────────────────────
// Parsers — invalid values fall back to null, never crash
// ────────────────────────────────────────────────────────────────

export function parseViewParam(value: unknown): ViewParam | null {
  return value === 'classify' || value === 'deposit-match' ? value : null;
}

export function parseActionParam(value: unknown): ActionParam | null {
  return value === 'refund' || value === 'rapid' ? value : null;
}

export function parseReturnToParam(value: unknown): ReturnToParam | null {
  return value === 'report' ? value : null;
}

const STATUS_PIVOTS: ReadonlyArray<StatusPivot> = [
  'unattributed',
  'staged',
  'reported',
  'notreported',
  'all',
];

export function parseStatusPivotParam(value: unknown): StatusPivot | null {
  return typeof value === 'string' && (STATUS_PIVOTS as readonly string[]).includes(value)
    ? (value as StatusPivot)
    : null;
}

function parseSignParam(value: unknown): BankTransactionSignFilter | null {
  if (typeof value !== 'string') return null;
  return Object.values(BankTransactionSignFilter).includes(
    value as BankTransactionSignFilter,
  )
    ? (value as BankTransactionSignFilter)
    : null;
}

function parseAttributionParam(
  value: unknown,
): BankTransactionStatusFilter | null {
  if (typeof value !== 'string') return null;
  return Object.values(BankTransactionStatusFilter).includes(
    value as BankTransactionStatusFilter,
  )
    ? (value as BankTransactionStatusFilter)
    : null;
}

// ────────────────────────────────────────────────────────────────
// Locked key names — the contract CPH-011 leans on
// ────────────────────────────────────────────────────────────────

// USI-005 (ADR-011): the CPH-021 cross-lens wormhole keys were dropped with
// the reconciliation workspace. The ADR explicitly rules out reintroducing
// the "lens" concept inside the unified inbox.
export const SEARCH_PARAM_NAMES = {
  status: 'status',
  dateFrom: 'dateFrom',
  dateTo: 'dateTo',
  action: 'action',
  returnTo: 'returnTo',
  reportId: 'reportId',
  contributionId: 'contributionId',
  // Mirrors contributionId. The report-detail expenditure rows deep-link the
  // Close ledger to the row's transaction so the treasurer can fix/confirm it.
  expenditureId: 'expenditureId',
  // GitHub #61 — the server-computed amount a cure should propose, carried from the preflight
  // warning's Resolve affordance to the resolution dialog it opens. Transport only: the figure
  // is computed and clamped server-side, never derived here.
  excess: 'excess',
} as const;

export type SearchParamName =
  (typeof SEARCH_PARAM_NAMES)[keyof typeof SEARCH_PARAM_NAMES];

// ────────────────────────────────────────────────────────────────
// Sort sub-route search shape — shared by the rev 3 contribution-centric
// `/close/sort` workspace and the `/close/file` Phase 3 route. The shape
// is wider than either route strictly needs because it doubles as the
// underlying MRP search vocabulary (consumed when MRP is mounted by either
// route), so a deep-link from the bank workspace into a Close sub-route
// round-trips its filter state.
// ────────────────────────────────────────────────────────────────

export interface SortRouteSearch {
  status?: StatusPivot;
  attribution?: BankTransactionStatusFilter;
  account?: string;
  sign?: BankTransactionSignFilter;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  deposit?: string;
  processorId?: string;
  action?: ActionParam;
  contributionId?: string;
  expenditureId?: string;
  excess?: number;
  returnTo?: ReturnToParam;
  reportId?: string;
}

/**
 * `validateSearch` callback for `/close/sort` (CPH-016 rev 3) and
 * `/close/file` (CPH-014). Both routes share the underlying MRP search
 * vocabulary so a deep-link's filter state survives the cross-route hop.
 */
export function parseSortRouteSearch(
  search: Record<string, unknown>,
): SortRouteSearch {
  return {
    status: parseStatusPivotParam(search.status) ?? undefined,
    attribution: parseAttributionParam(search.attribution) ?? undefined,
    account: typeof search.account === 'string' ? search.account : undefined,
    sign: parseSignParam(search.sign) ?? undefined,
    dateFrom: typeof search.dateFrom === 'string' ? search.dateFrom : undefined,
    dateTo: typeof search.dateTo === 'string' ? search.dateTo : undefined,
    search: typeof search.search === 'string' ? search.search : undefined,
    page:
      typeof search.page === 'number' && search.page > 0 ? search.page : undefined,
    deposit: typeof search.deposit === 'string' ? search.deposit : undefined,
    processorId:
      typeof search.processorId === 'string' ? search.processorId : undefined,
    action: parseActionParam(search.action) ?? undefined,
    contributionId:
      typeof search.contributionId === 'string'
        ? search.contributionId
        : undefined,
    expenditureId:
      typeof search.expenditureId === 'string'
        ? search.expenditureId
        : undefined,
    // Only a positive number is a usable pre-fill; anything else is dropped so a mangled link
    // degrades to an empty amount field rather than proposing nonsense.
    excess:
      typeof search.excess === 'number' && search.excess > 0
        ? search.excess
        : undefined,
    returnTo: parseReturnToParam(search.returnTo) ?? undefined,
    reportId: typeof search.reportId === 'string' ? search.reportId : undefined,
  };
}

// USI-005 (ADR-011): `ReconcileShellRouteSearch` + `parseReconcileShellSearch`
// were retired with the `/close/reconcile` route and the four-tab cluster.
